// WI-31 kid hooks: one or two child-facing lines per place, shown on a child's stage card on the
// Trip tab (and later in the Crate's empty drawer) to make them want to go and look.
//
// ***************************************************************************************
// DRAFT, FOR OWNER REVIEW (lane 4, run of 25 Oct 2026). Not switched on: KID_HOOKS_LIVE is
// false, so kidHook() returns null and nothing shows. The owner reviews the wording (the
// mockup is docs/renders/kid-hooks/), then flips KID_HOOKS_LIVE.
// ***************************************************************************************
//
// The writing rules the drafts follow:
// - Talk to the child ("you"), at a 6–10-year-old's reading level, 12 words or fewer a line.
// - Tease, don't spoil: a rare act is hinted at ("something white with a fluffy tail"); a
//   legendary one is a mystery ("something huge sleeps under the snow").
// - One line is something to spot, the other something to do or taste, from the place's own acts
//   (src/map/places/actCatalog.ts), true in every season the place shows unless marked.
// - No family names, no grown-up roles, no brand names.

export const KID_HOOKS_LIVE = false

export interface KidHook {
  /** Something to look for (a hint at one of the place's acts). */
  spot: string
  /** Something to do, taste or hear there. */
  try: string
  /** Lines that only make sense in one season (the stage's season picks it). */
  winter?: string
  summer?: string
}

export const KID_HOOKS_DRAFT: Record<string, KidHook> = {
  sapporo: {
    spot: 'A red fox sneaks through the park. Can you spot its tail?',
    try: 'Slurp a bowl of miso ramen as loudly as you like.',
    winter: 'Giant snow sculptures. One of them might wake up…',
    summer: 'A fox made of corn hides somewhere in the park.',
  },
  niseko: {
    spot: 'Keep an eye on the chairlift. Something hairy rides it.',
    try: 'Wave at the ski patrol and listen for the snowcat.',
    winter: 'Fresh snow every night. Who will fall over first?',
  },
  otaru: {
    spot: 'A spotted seal pops up in the harbour. Count its whiskers!',
    try: 'Hear the steam clock whistle and the giant music box play.',
    winter: 'Candles in the snow light the canal path at night.',
    summer: 'Watch out: the gulls want your snack!',
  },
  tokyo: {
    spot: 'A famous bronze dog waits at the station. Does it move?',
    try: 'Cross the busiest crossing in the world with hundreds of people.',
  },
  kyoto: {
    spot: 'A white fox guards the red gates. Run through a thousand of them!',
    try: 'Ring the big temple bell. Bonnng!',
  },
  osaka: {
    spot: 'There’s an octopus in the canal. Really!',
    try: 'Flip a takoyaki ball, then copy the running-man pose.',
  },
  nara: {
    spot: 'Hundreds of deer, and one is snowy white.',
    try: 'Bow to a deer. It might bow back!',
  },
  miyajima: {
    spot: 'A deer carries a map. Where is it going?',
    try: 'Walk to the giant gate in the sea when the tide goes out.',
  },
  kamakura: {
    spot: 'A sea turtle swims past the surfers. Look for its shell.',
    try: 'Step inside the Great Buddha. He’s hollow!',
  },
  fuji: {
    spot: 'Watch Fuji’s top. Sometimes it wears a cloud hat.',
    try: 'Eat a black egg from the steaming valley.',
  },
  shirakawago: {
    spot: 'A black bear lives in the hills. Shh, don’t wake it.',
    try: 'See how thick the snow sits on the pointy roofs.',
  },
  village: {
    spot: 'Something green and slippery lives in the river…',
    try: 'At five o’clock the village plays a song. Listen!',
  },
  castletown: {
    spot: 'A giant salamander hides under the bridge. It’s as long as you!',
    try: 'Spin the lottery wheel at the shopping street.',
  },
  bigcity: {
    spot: 'A wild boar wanders into town. Who will spot it first?',
    try: 'Watch the bullet train get cleaned in seven minutes.',
  },
  fukuoka: {
    spot: 'Turtles sunbathe on the pond. How many can you count?',
    try: 'Ask for “kaedama!” and get more noodles.',
  },
  okinawa: {
    spot: 'A whale shark as big as a bus swims in the bay.',
    try: 'The lion-dog statues on the roofs might wake up. Wave at them!',
  },
  seoul: {
    spot: 'A cheeky magpie steals snacks. Hold on to yours!',
    try: 'Watch the palace guards change, with drums and bright colours.',
    winter: 'Snowball fight in the old village. Duck!',
  },
  hangang: {
    spot: 'A river otter lives under the bridges. Look for a whiskery nose.',
    try: 'Watch the bridge become a rainbow fountain.',
    winter: 'Whoosh down the sledding hill by the river.',
  },
}

/** The hook for a place on a child's stage card, or null (always null until KID_HOOKS_LIVE). */
export function kidHook(place: string, season: 'winter' | 'summer' | string | null): string[] | null {
  if (!KID_HOOKS_LIVE) return null
  return kidHookLines(place, season)
}

/** The lines regardless of the switch (the review page and tests). */
export function kidHookLines(place: string, season: 'winter' | 'summer' | string | null): string[] | null {
  const h = KID_HOOKS_DRAFT[place]
  if (!h) return null
  const seasonal = season === 'winter' ? h.winter : season === 'summer' ? h.summer : undefined
  return [seasonal ?? h.spot, h.try]
}
